// checkinSlice.ts
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { FlightInfo, Seat } from "@repo/ui/types/seats";

import { useAppSelector } from "../hooks";
import type { Passenger as PassengerInfo } from "./passengerSlice";

// Type for passenger field values
type PassengerFieldValue = string | number | boolean | object | null | undefined;

// ---------- Types ----------
export interface CheckinFlightSegment {
  id: string; // unique segment identifier
  flightInfo: FlightInfo;
  seats?: Seat[];
  passengers: PassengerInfo[];
  arrivalStationCode?: string;
  departureStationCode?: string;
  arrivalDate?: string;
  departureDate?: string;
}

export interface EmergencyContact {
  name: string;
  phone: string;
  email: string;
}

export interface CheckinFlowState {
  outbound: CheckinFlightSegment[];
  inbound: CheckinFlightSegment[];
  passengers: PassengerInfo[];
  journeyType: "oneway" | "roundtrip";
  departure: string;
  destination: string;
  travelDates: { from: string; to?: string; fromUTC?: string; toUTC?: string };
  isCheckinFlow?: boolean;
  timeVariant?: {
    outbound: number;
    inbound: number;
  };
  passengersCount: number;
  prohibitedItemsDeclared?: boolean;
  isHydrated: boolean; // optional flag to indicate slice has been initialized
  recordLocator?: string;
  departureCode?: string;
  destinationCode?: string;
  departureCountry?: string;
  destinationCountry?: string;
  inboundSubtotal?: number;
  outboundSubtotal?: number;
}

// ---------- Initial state ----------
const initialState: CheckinFlowState = {
  outbound: [],
  inbound: [],
  timeVariant: {
    outbound: 0,
    inbound: 0,
  },
  passengers: [],
  journeyType: "oneway",
  departure: "",
  destination: "",
  travelDates: { from: "" },
  passengersCount: 0,
  isHydrated: false,
  recordLocator: "",
  departureCode: "",
  destinationCode: "",
  departureCountry: "",
  destinationCountry: "",
  isCheckinFlow: false,
  inboundSubtotal: 0,
  outboundSubtotal: 0,
};

// ---------- Slice ----------
const checkinSlice = createSlice({
  name: "checkinFlow",
  initialState,
  reducers: {
    setCheckinFlow: (state, action: PayloadAction<Partial<CheckinFlowState>>) => {
      const { inboundSubtotal, outboundSubtotal } = state;
      const payload = action.payload;

      Object.assign(state, payload);

      if (payload.inboundSubtotal === undefined) {
        state.inboundSubtotal = inboundSubtotal;
      }

      if (payload.outboundSubtotal === undefined) {
        state.outboundSubtotal = outboundSubtotal;
      }

      state.isHydrated = true;
      state.isCheckinFlow = true;
    },
    resetCheckinFlow: () => initialState,
    updateProhibitedItemsDeclaration: (state, action: PayloadAction<boolean>) => {
      state.prohibitedItemsDeclared = action.payload;
    },
    updateCheckinPassengerField: (
      state,
      action: PayloadAction<{
        index: number;
        field: keyof PassengerInfo;
        value: PassengerFieldValue;
        subField?: string;
      }>,
    ) => {
      const { index, field, value, subField } = action.payload;
      if (index >= 0 && index < state.passengers.length) {
        const passenger = state.passengers[index];
        if (passenger) {
          if (subField && typeof passenger[field] === "object" && passenger[field] !== null) {
            (passenger[field] as Record<string, PassengerFieldValue>)[subField] = value;
          } else {
            (passenger as Record<string, PassengerFieldValue>)[field] = value;
          }
        }
      }
    },
    updateCheckinPassengerAdditionalInfo: (
      state,
      action: PayloadAction<{
        index: number;
        field: string;
        value: PassengerFieldValue;
      }>,
    ) => {
      const { index, field, value } = action.payload;
      if (index >= 0 && index < state.passengers.length) {
        const passenger = state.passengers[index];
        if (passenger) {
          passenger.additionalInformation ??= {};
          (passenger.additionalInformation as Record<string, PassengerFieldValue>)[field] = value;
        }
      }
    },
    updateCheckinPassengerInfant: (
      state,
      action: PayloadAction<{
        parentIndex: number;
        field: string;
        value: PassengerFieldValue;
      }>,
    ) => {
      const { parentIndex, field, value } = action.payload;
      if (parentIndex >= 0 && parentIndex < state.passengers.length) {
        const passenger = state.passengers[parentIndex];
        if (passenger?.infant) {
          // Handle nested field updates (e.g., "dateOfBirth.day", "documentExpirationDate.month")
          const fieldParts = field.split(".").filter(Boolean);
          if (fieldParts.length > 1) {
            let current: Record<string, PassengerFieldValue> = passenger.infant;

            // Navigate to the nested object
            for (let i = 0; i < fieldParts.length - 1; i++) {
              const key = fieldParts[i];
              if (key && (typeof current[key] !== "object" || current[key] === null)) {
                current[key] = {};
              }
              if (key) {
                current = current[key] as Record<string, PassengerFieldValue>;
              }
            }

            // Set the final value
            const lastKey = fieldParts.at(-1);
            if (lastKey) {
              current[lastKey] = value;
            }
          } else {
            (passenger.infant as Record<string, PassengerFieldValue>)[field] = value;
          }
        }
      }
    },
    assignSeat: (
      state,
      action: PayloadAction<{ segmentId: string; passengerId: number; seat: Seat | null }>,
    ) => {
      const { segmentId, passengerId, seat } = action.payload;

      const updateSegment = (segments: CheckinFlightSegment[]) => {
        const segment = segments.find((s) => s.id === segmentId);
        if (segment) {
          const passenger = segment.passengers.find((p) => p.id === passengerId);
          if (passenger) {
            passenger.seat = seat;
          }
        }
      };

      updateSegment(state.outbound);
      updateSegment(state.inbound);
    },

    setCheckinInboundSubtotal: (
      state,
      action: PayloadAction<{
        amount: number;
      }>,
    ) => {
      const { amount } = action.payload;
      state.inboundSubtotal = amount;
    },

    setCheckinOutboundSubtotal: (
      state,
      action: PayloadAction<{
        amount: number;
      }>,
    ) => {
      const { amount } = action.payload;
      state.outboundSubtotal = amount;
    },
    updateCheckinCountries: (
      state,
      action: PayloadAction<{ departureCountry: string; destinationCountry: string }>,
    ) => {
      state.departureCountry = action.payload.departureCountry;
      state.destinationCountry = action.payload.destinationCountry;
    },
  },
});

// ---------- Exports ----------
export const {
  setCheckinFlow,
  resetCheckinFlow,
  updateProhibitedItemsDeclaration,
  updateCheckinPassengerField,
  updateCheckinPassengerAdditionalInfo,
  updateCheckinPassengerInfant,
  assignSeat,
  setCheckinInboundSubtotal,
  setCheckinOutboundSubtotal,
  updateCheckinCountries,
} = checkinSlice.actions;
export default checkinSlice.reducer;

// ---------- Selectors / Hooks ----------
export const useCheckinFlow = () => useAppSelector((s) => s.checkinFlow);
export const useCheckinOutbound = () => useAppSelector((s) => s.checkinFlow.outbound);
export const useCheckinInbound = () => useAppSelector((s) => s.checkinFlow.inbound);
export const useCheckinPassengersCount = () => useAppSelector((s) => s.checkinFlow.passengersCount);
export const useCheckinJourneyType = () => useAppSelector((s) => s.checkinFlow.journeyType);
export const useCheckinPassengers = () => useAppSelector((s) => s.checkinFlow.passengers);
export const useCheckinCountries = () =>
  useAppSelector((s) => ({
    departureCountry: s.checkinFlow.departureCountry,
    destinationCountry: s.checkinFlow.destinationCountry,
  }));

// Hook to get grand total of extras (inbound + outbound subtotals)
export const useCheckingGrandTotal = () => {
  const outbound = useAppSelector((state) => state.checkinFlow.outboundSubtotal ?? 0);
  const inbound = useAppSelector((state) => state.checkinFlow.inboundSubtotal ?? 0);

  return outbound + inbound;
};
